import React, { useState, useRef, useEffect } from 'react';
import { HelpCircle } from 'lucide-react';

interface HintBadgeProps {
  title?: string;
  content: React.ReactNode;
  /** Where the popover should open. Default 'right'. 'top-end' opens above and right-aligned
   * (grows leftward) — for badges at the right edge of a clipped container. */
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'top-end';
  /** Optional className for the badge button itself. */
  className?: string;
}

/**
 * Small ? icon that shows an explanation popover on hover or click.
 * Inline-friendly: drop next to any control that benefits from a short
 * explanation without cluttering the UI.
 */
export const HintBadge: React.FC<HintBadgeProps> = ({
  title,
  content,
  placement = 'right',
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<number | null>(null);

  // Close when clicking outside
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, [open]);

  const scheduleClose = () => {
    if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = window.setTimeout(() => setOpen(false), 200);
  };

  const cancelClose = () => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  // Popover placement classes
  const popoverPositionClass = {
    top: 'bottom-full mb-2 left-1/2 -translate-x-1/2',
    bottom: 'top-full mt-2 left-1/2 -translate-x-1/2',
    left: 'right-full mr-2 top-1/2 -translate-y-1/2',
    right: 'left-full ml-2 top-1/2 -translate-y-1/2',
    'top-end': 'bottom-full mb-2 right-0',
  }[placement];

  return (
    <div
      ref={rootRef}
      className={`relative inline-flex items-center ${className}`}
      onMouseEnter={() => { cancelClose(); setOpen(true); }}
      onMouseLeave={scheduleClose}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(o => !o);
        }}
        className="p-0.5 rounded text-ink-3 hover:text-primary transition-colors"
        aria-label={title || 'More info'}
      >
        <HelpCircle className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div
          className={`absolute z-50 w-64 bg-navy text-white rounded-lg shadow-xl border border-white/10 p-3 ${popoverPositionClass}`}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
          role="tooltip"
        >
          {title && (
            <h4 className="text-[11px] font-semibold text-white mb-1">{title}</h4>
          )}
          <div className="text-[11px] text-white/80 leading-relaxed">
            {content}
          </div>
        </div>
      )}
    </div>
  );
};
